import {
  useFormattedSites,
  useGetPeriodOptions,
} from '@app/ccf/company-report/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGetSites } from '@shared/api';
import { useGetOrganizationTimePeriods } from '@shared/api/hooks/ccf/periods';
import {
  useDeleteSoldPackagingItemRecord,
  usePatchSoldPackagingItemRecord,
} from '@shared/api/hooks/sold-goods/packaging';
import { PackagingOperationActivityRecord } from '@shared/api/types';
import InputFormField from '@shared/components/form/input-form-field';
import SelectFormField from '@shared/components/form/select-form-field';
import SelectSearchFormField from '@shared/components/form/select-search-form-field';
import { Button } from '@shared/components/ui/button';
import { Form } from '@shared/components/ui/form';
import { Skeleton } from '@shared/components/ui/skeleton';
import { useToast } from '@shared/components/ui/use-toast';
import sentry from '@shared/services/sentry';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { object, string, z } from 'zod';
import { useSoldPackagingItem } from '../../sold-packaging-item-context';

interface SoldPackagingItemSalesEditFormProps {
  packagingActivityRecord?: PackagingOperationActivityRecord;
  onOpenChange?: (open: boolean) => void;
  numberOfSales: number;
}

export const packagingItemSalesSchema = object({
  uuid: string().min(1),
  site: string().optional(),
  customer: string().optional(),
  totalQuantity: z.coerce.number().min(0.001),
  timePeriod: string().min(1),
  quantity: z.coerce.number().min(1),
});

export default function SoldPackagingItemSalesEditForm({
  packagingActivityRecord,
  onOpenChange,
  numberOfSales,
}: SoldPackagingItemSalesEditFormProps) {
  const { toast } = useToast();
  const { item } = useSoldPackagingItem();

  const {
    mutateAsync: updatePackagingSale,
    isLoading: isUpdatingPackagingSale,
  } = usePatchSoldPackagingItemRecord();

  const {
    mutateAsync: deletePackagingSale,
    isLoading: isDeletingPackagingSale,
  } = useDeleteSoldPackagingItemRecord();

  const { data: sites, isLoading: isSitesLoading } =
    useGetSites({
      page: 1,
      page_size: 1000,
    }) || [];

  const formattedSites = useFormattedSites(sites?.results || []);

  const { data: periods, isLoading: isPeriodsLoading } =
    useGetOrganizationTimePeriods();
  const getPeriodOptions = useGetPeriodOptions(periods!);

  const form = useForm<z.infer<typeof packagingItemSalesSchema>>({
    resolver: zodResolver(packagingItemSalesSchema),
  });

  const { reset, formState, watch, setValue } = form;

  const onSubmit = async (data: z.infer<typeof packagingItemSalesSchema>) => {
    const updateData = {
      timePeriod: data.timePeriod,
      quantity: data.quantity,
      uuid: data.uuid,
      totalQuantity: data.totalQuantity,
    };

    try {
      await updatePackagingSale({
        activityUuid: item.uuid,
        recordUuid: data.uuid,
        data: updateData,
      });

      onOpenChange?.(false);
      toast({
        title: 'Sale updated',
        variant: 'success',
      });
    } catch (error) {
      sentry.log(error);
      toast({
        title: 'Failed to update sale',
        variant: 'destructive',
      });
    }
  };

  const onDelete = async () => {
    try {
      await deletePackagingSale({
        activityUuid: item.uuid,
        recordUuid: packagingActivityRecord!.uuid,
      });
      onOpenChange?.(false);
      toast({
        title: 'Sale deleted',
        variant: 'success',
      });
    } catch (error) {
      sentry.log(error);
      toast({
        title: 'Failed to delete sale',
        variant: 'destructive',
      });
    }
  };

  const quantity = watch('quantity');
  const itemsPerPack = item.itemsPerPack;

  const setTotalQuantity = useMemo(() => {
    return (quantity: number, itemsPerPack: number) => {
      setValue('totalQuantity', quantity * itemsPerPack);
    };
  }, [setValue]);

  useEffect(() => {
    setTotalQuantity(quantity, itemsPerPack);
  }, [itemsPerPack, quantity, setTotalQuantity]);

  useEffect(() => {
    if (packagingActivityRecord) {
      reset({
        timePeriod: packagingActivityRecord.timePeriod.uuid,
        quantity: packagingActivityRecord.quantity,
        uuid: packagingActivityRecord.uuid,
        totalQuantity: Number(packagingActivityRecord.totalQuantity),
        customer: packagingActivityRecord.customer?.name || '',
      });
    }
  }, [packagingActivityRecord, reset]);

  if (isPeriodsLoading || isSitesLoading) {
    return (
      <div className="grid grid-cols-12 items-end gap-2">
        <Skeleton className="col-span-3 h-10 rounded-lg" />
        <Skeleton className="col-span-3 h-10 rounded-lg" />
        <Skeleton className="col-span-3 h-10 rounded-lg" />
        <Skeleton className="col-span-3 h-10 rounded-lg" />
      </div>
    );
  }

  if (!periods) {
    throw new Error(
      `Failed to load periods for food activity rows ${isPeriodsLoading || 'No data'}`
    );
  }

  return (
    <Form {...form}>
      <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
        <div className="grid grid-cols-12 items-start gap-2">
          <SelectFormField
            name="timePeriod"
            options={getPeriodOptions}
            disabled
            label="Period"
            className="col-span-3"
            loading={isPeriodsLoading}
          />
          <SelectSearchFormField
            name="site"
            pluralName="sites"
            options={formattedSites}
            label="Site"
            disabled={isUpdatingPackagingSale || isDeletingPackagingSale}
            className="col-span-3"
          />
          <InputFormField
            name="customer"
            label="Customer"
            disabled
            className="col-span-3"
          />
          <InputFormField
            name="quantity"
            label="Packs sold"
            className="col-span-3"
            type="number"
            disabled={isUpdatingPackagingSale || isDeletingPackagingSale}
          />
          <InputFormField
            name="totalQuantity"
            label="Total quantity"
            className="col-span-3"
            disabled
          />
        </div>
        <div className="flex justify-between">
          <Button
            type="button"
            variant="destructive"
            onClick={() => onDelete()}
            disabled={isUpdatingPackagingSale || numberOfSales <= 1}
            loading={isUpdatingPackagingSale || isDeletingPackagingSale}
          >
            Delete sale
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange?.(false)}
              disabled={isUpdatingPackagingSale}
              loading={isUpdatingPackagingSale || isDeletingPackagingSale}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isUpdatingPackagingSale || isDeletingPackagingSale}
              disabled={isUpdatingPackagingSale || !formState.isDirty}
            >
              {isUpdatingPackagingSale ? 'Updating...' : 'Update'}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}
